import * as types from './mutation-types'
import Vue from 'vue'
import { MutationTree } from 'vuex/types'
import { buildMissionUIStateId, MissionUiState } from '@/models/ui/MissionUiState'
import { IUiState } from './state'
import { GraphUiState } from '@/models/ui/GraphUiState'
import { MapUiState } from '@/models/ui/MapUiState'
import { AllGraphUiState } from '@/models/ui/AllGraphUiState'
import { Coordinate } from 'ol/coordinate'
import { ZoomTransform } from 'd3'
import { ConsoleRow } from '@/models/ui/ConsoleRow'
import { buildFleetUIStateId, FleetUiState } from '@/models/ui/FleetUiState'
import { buildMissionPlanStateId, MissionPlanUiState } from '@/models/ui/MissionPlanUiState'
import { HoverData } from '@/models/ui/HoverData'
import { LayerConfig } from '@/models/ui/LayerConfig'
import { nextTick } from 'vue/types/umd'
import { GraphNmeaState } from '@/models/ui/GraphNmeaState'
import { SoundNotification } from '@/enum/SoundNotification'
import { buildSimulationUIStateId, SimulationUiState } from '@/models/ui/SimulationUiState'
import { buildDefaultSmartPlanningUiState } from '@/models/ui/SmartPlanningUiState'
import { MISSIONS_DETAIL_TABS } from '@/models/ui/MissionTabs'
import { UiTab } from '@/models/ui/UiTab'
import { SIMULATIONS_DETAIL_TABS } from '@/models/ui/SimulationTabs'
import { UiTabs } from '@/models/ui/UiTabs'
import { GraphSimulationState } from '@/models/ui/GraphSimulationState'
import { AloLogbookItem } from '@/models/alo/AloLogbookItem'
import apiProxy from '@/api/proxy'

const mutations: MutationTree<IUiState> = {
  [types.UI_CLOSE_SNACKBAR]: state => {
    state.snackMessages = []
  },
  [types.UI_ADD_SNACK_MESSAGE]: (state, message: string) => {
    state.snackMessages.splice(0, 0, message)
  },
  [types.UI_SET_REDIRECT_PATH]: (state, path: string) => {
    state.redirectPath = path
  },
  [types.UI_SET_SOUND_NOTIFICATION]: (state, soundNotification: SoundNotification) => {
    state.soundNotification = soundNotification
    localStorage.setItem('soundNotification', soundNotification)
  },
  [types.UI_TOGGLE_DRAWER_VISIBILITY]: state => {
    state.draweropen = !state.draweropen
  },
  [types.UI_RESET_SMARTPLANNING_FILTER]: state => {
    state.smartPlanningUiState = {
      ...buildDefaultSmartPlanningUiState(),
      filterPanelVisible: state.smartPlanningUiState.filterPanelVisible,
    }
  },
  [types.UI_TOGGLE_GDB_CONSOLE_FILTER]: (state, { gdbType, uiid }: { gdbType: string; uiid: string }) => {
    const consoleGdbFilter = state.uistates[uiid].consoleGdbFilter
    const idx = consoleGdbFilter.indexOf(gdbType)
    if (idx > -1) consoleGdbFilter.splice(idx, 1)
    else consoleGdbFilter.push(gdbType)
  },
  [types.UI_SET_GDB_CONSOLE_FILTER]: (state, { gdbTypes, uiid }: { gdbTypes: Array<string>; uiid: string }) => {
    Vue.set(state.uistates[uiid], 'consoleGdbFilter', gdbTypes)
  },
  [types.UI_SET_CONSOLE_AUTOSCROLL]: (state, { autoScroll, uiid }: { autoScroll: boolean; uiid: string }) => {
    Vue.set(state.uistates[uiid], 'consoleAutoScroll', autoScroll)
  },
  [types.UI_SET_CONSOLE_SCROLLINDEX]: (state, { index, uiid }: { index: number; uiid: string }) => {
    Vue.set(state.uistates[uiid], 'consoleScrollIndex', index)
  },
  [types.UI_TOGGLE_DARKMODE]: state => {
    state.darkmode = !state.darkmode
  },
  [types.UI_SET_DRAWER_VISIBILITY]: (state, isVisible: boolean) => {
    state.draweropen = isVisible
  },
  [types.UI_TOGGLE_RIGHT_DRAWER_VISIBILITY]: state => {
    state.rightdraweropen = !state.rightdraweropen
  },
  [types.UI_SET_RIGHT_DRAWER_VISIBILITY]: (state, isVisible: boolean) => {
    state.rightdraweropen = isVisible
  },
  [types.UI_TOGGLE_MISSION_MAP_VISIBILITY]: (state, { missionId, uiid }: { missionId: number; uiid: string }) => {
    if (uiid == '') {
      Vue.set(state.missionMapVisibility, missionId, !state.missionMapVisibility[missionId])
    } else {
      Vue.set(
        state.uistates[uiid].missionMapVisibility,
        missionId,
        !state.uistates[uiid].missionMapVisibility[missionId],
      )
    }
  },
  [types.UI_SET_MISSION_MAP_VISIBILITY]: (
    state,
    { missionId, uiid, visible }: { missionId: number; uiid: string; visible: boolean },
  ) => {
    if (uiid == '') {
      Vue.set(state.missionMapVisibility, missionId, visible)
    } else {
      Vue.set(state.uistates[uiid].missionMapVisibility, missionId, visible)
    }
  },
  [types.UI_SHOW_ALL_DRAWER_MISSION_MAP]: (
    state,
    { newMissionMapVisibility, uiid }: { newMissionMapVisibility: { [id: number]: boolean }; uiid: string },
  ) => {
    if (uiid == '') {
      Vue.set(state, 'missionMapVisibility', { ...state.missionMapVisibility, ...newMissionMapVisibility })
      Vue.set(state, 'allMissionVisibility', true)
    } else {
      Vue.set(state.uistates[uiid], 'missionMapVisibility', newMissionMapVisibility)
      Vue.set(state.uistates[uiid], 'allMissionVisibility', true)
    }
  },
  [types.UI_HIDE_ALL_DRAWER_MISSION_MAP]: (
    state,
    { newMissionMapVisibility, uiid }: { newMissionMapVisibility: { [id: number]: boolean }; uiid: string },
  ) => {
    if (uiid == '') {
      Vue.set(state, 'missionMapVisibility', { ...state.missionMapVisibility, ...newMissionMapVisibility })
      Vue.set(state, 'allMissionVisibility', false)
    } else {
      Vue.set(state.uistates[uiid], 'missionMapVisibility', newMissionMapVisibility)
      Vue.set(state.uistates[uiid], 'allMissionVisibility', false)
    }
  },
  [types.UI_SHOW_ALL_SIMULATION_MISSION_MAP]: (state, newSimulationMapVisibility: { [id: number]: boolean }) => {
    Vue.set(state, 'simulationMapVisibility', { ...state.simulationMapVisibility, ...newSimulationMapVisibility })
    Vue.set(state, 'allSimulationVisibility', true)
  },
  [types.UI_HIDE_ALL_SIMULATION_MISSION_MAP]: (state, newSimulationMapVisibility: { [id: number]: boolean }) => {
    Vue.set(state, 'simulationMapVisibility', { ...state.simulationMapVisibility, ...newSimulationMapVisibility })
    Vue.set(state, 'allSimulationVisibility', false)
  },
  [types.UI_TOGGLE_MISSION_INFOS_VISIBILITY]: (state, { missionId, uiid }: { missionId: number; uiid: string }) => {
    if (uiid == '') {
      Vue.set(state.missionDetailVisibility, missionId, !state.missionDetailVisibility[missionId])
    } else {
      Vue.set(
        state.uistates[uiid].missionDetailVisibility,
        missionId,
        !state.uistates[uiid].missionDetailVisibility[missionId],
      )
    }
  },
  [types.UI_SELECT_MAP_BASELAYER]: (state, { layer, uiid }: { layer: any; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].baseMapLayer = layer
  },
  [types.UI_UNSELECT_MAP_BASELAYER]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].baseMapLayer = { id: 'OSM' }
  },
  [types.UI_CENTER_MAP]: (state, { center, uiid }: { center: Coordinate; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].mapCenter = center
  },
  [types.UI_ZOOM_MAP]: (state, { zoom, uiid }: { zoom: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].mapZoom = zoom
  },
  [types.UI_POS_MAP]: (state, { center, zoom, uiid }: { center: Coordinate; zoom: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].mapCenter = center
      state.uistates[uiid].mapZoom = zoom
    }
  },
  [types.UI_REFRESH_BEST_FIT_MAP]: (state, { uiid, bestFitMisId }: { uiid: string; bestFitMisId: number }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].bestFitMisId = bestFitMisId
      state.uistates[uiid].refreshBestFit = !state.uistates[uiid].refreshBestFit
    }
  },
  [types.UI_SELECT_MISSION_MAP]: (state, id: number) => {
    state.currentMissionMapId = id
  },
  [types.UI_SELECT_SIMULATION_MAP]: (state, id: number) => {
    state.currentMissionMapId = id
  },
  [types.UI_SELECT_TIMELINE_MISSION_INDEX]: (state, { index, uiid }: { index: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].timelineIndex = index
  },
  [types.UI_REFRESH_NAV_GRAPH_CACHE]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].refreshNavGraphCache = !state.uistates[uiid].refreshNavGraphCache
  },

  [types.UI_REFRESH_SCI_GRAPH_CACHE]: (
    state,
    { uiid, importedCsvHeaders }: { uiid: string; importedCsvHeaders: Array<string> | null },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].importedCsvHeaders = importedCsvHeaders
      state.uistates[uiid].refreshSciGraphCache = !state.uistates[uiid].refreshSciGraphCache
    }
  },
  [types.UI_SELECT_RANGETIMELINE_MISSION_INDEX]: (state, { range, uiid }: { range: number[]; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].rangeTimelineIndex = range
  },
  [types.UI_TOGGLE_HEADING_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showHeading = !state.uistates[uiid].showHeading
  },
  [types.UI_SHOW_HEADING]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showHeading = true
  },
  [types.UI_HIDE_HEADING]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showHeading = false
  },
  [types.UI_TOGGLE_OCEANCURRENT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].showOceanCurrent = !state.uistates[uiid].showOceanCurrent
  },
  [types.UI_SHOW_OCEANCURRENT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showOceanCurrent = true
  },
  [types.UI_HIDE_OCEANCURRENT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showOceanCurrent = false
  },
  [types.UI_SET_BORNE_MIN_CURRENT]: (state, { min, uiid }: { min: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].currentMin = min
    }
  },
  [types.UI_SET_BORNE_MAX_CURRENT]: (state, { max, uiid }: { max: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].currentMax = max
    }
  },
  [types.UI_SET_ZOOM_CURRENT]: (state, { zoom, uiid }: { zoom: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].currentZoom = zoom
    }
  },
  [types.UI_TOGGLE_DRIFT_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showDrift = !state.uistates[uiid].showDrift
  },
  [types.UI_SHOW_DRIFT]: (state, uiid: string) => {
    state.uistates[uiid].showDrift = true
  },
  [types.UI_HIDE_DRIFT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showDrift = false
  },
  [types.UI_TOGGLE_MISSIONPLAN_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].showMissionPlan = !state.uistates[uiid].showMissionPlan
  },
  [types.UI_SHOW_MISSIONPLAN]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMissionPlan = true
  },
  [types.UI_HIDE_MISSIONPLAN]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMissionPlan = false
  },
  [types.UI_TOGGLE_MISSIONPLAN_VALIDATION_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].showMissionPlanValidation = !state.uistates[uiid].showMissionPlanValidation
  },
  [types.UI_SHOW_MISSIONPLAN_VALIDATION]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMissionPlanValidation = true
  },
  [types.UI_HIDE_MISSIONPLAN_VALIDATION]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMissionPlanValidation = false
  },
  [types.UI_TOGGLE_MISSIONLAYER_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].showMissionLayer = !state.uistates[uiid].showMissionLayer
  },
  [types.UI_SET_MISSIONLAYERMAP_OPACITY]: (state, { opacity, uiid }: { opacity: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].missionLayermapOpacity = opacity
  },
  [types.UI_SHOW_MISSIONLAYER]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMissionLayer = true
  },
  [types.UI_HIDE_MISSIONLAYER]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMissionLayer = false
  },
  [types.UI_TOGGLE_ENDMISSION_SIMULATION_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].showEndMissionSimulation = !state.uistates[uiid].showEndMissionSimulation
  },
  [types.UI_SET_ENDMISSION_SIMULATION_OPACITY]: (state, { opacity, uiid }: { opacity: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].endMissionSimulationMapOpacity = opacity
  },
  [types.UI_SHOW_ENDMISSION_SIMULATION]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showEndMissionSimulation = true
  },
  [types.UI_HIDE_ENDMISSION_SIMULATION]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showEndMissionSimulation = false
  },
  [types.UI_TOGGLE_CYCLE_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showCycle = !state.uistates[uiid].showCycle
  },
  [types.UI_TOGGLE_AIS_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      if (!state.uistates[uiid].showAis) {
        apiProxy.authGeoserver().then(() => {
          state.uistates[uiid].showAis = !state.uistates[uiid].showAis
        })
      } else {
        state.uistates[uiid].showAis = !state.uistates[uiid].showAis
      }
    }
  },
  [types.UI_SHOW_CYCLE]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showCycle = true
  },
  [types.UI_HIDE_CYCLE]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showCycle = false
  },
  [types.UI_TOGGLE_WAYPOINT_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showWaypoint = !state.uistates[uiid].showWaypoint
  },
  [types.UI_SHOW_WAYPOINT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showWaypoint = true
  },
  [types.UI_HIDE_WAYPOINT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showWaypoint = false
  },
  [types.UI_TOGGLE_LAYERMAP_VISIBILITY]: (state, { layerId, uiid }: { layerId: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid))
      Vue.set(state.uistates[uiid].layermapVisibility, layerId, !state.uistates[uiid].layermapVisibility[layerId])
  },
  [types.UI_SHOW_LAYERMAP]: (state, { layerId, uiid }: { layerId: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates[uiid].layermapVisibility, layerId, true)
  },
  [types.UI_HIDE_LAYERMAP]: (state, { layerId, uiid }: { layerId: number; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates[uiid].layermapVisibility, layerId, false)
  },
  [types.UI_SET_LAYERMAP_OPACITY]: (
    state,
    { layerId, opacity, uiid }: { layerId: number; opacity: number; uiid: string },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates[uiid].layermapOpacity, layerId, opacity)
  },
  [types.UI_SET_LAYERMAP_CONFIG]: (
    state,
    { layerId, config, uiid }: { layerId: number; config: LayerConfig; uiid: string },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      Vue.set(state.uistates[uiid].layermapconfig, layerId, config)
      config.canRender = false
      Vue.nextTick(() => (config.canRender = true))
    }
  },
  [types.UI_MISSIONPLAN_SET_HOVER_WPT_ID]: (state, { uiid, wptId }: { uiid: string; wptId: number }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].hoverWptId = wptId
  },
  [types.UI_MISSIONPLAN_SET_HOVER_STEP_ID]: (state, { uiid, stepId }: { uiid: string; stepId: number }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].hoverStepId = stepId
  },
  [types.UI_MISSIONPLAN_SET_HOVER_PRF_ID]: (state, { uiid, prfId }: { uiid: string; prfId: number }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].hoverPrfId = prfId
  },
  [types.UI_MISSIONPLAN_SET_CURRENT_EDIT_PROFIL]: (state, { uiid, prf }: { uiid: string; prf: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].currentEditProfil = prf
  },
  [types.UI_MISSIONPLAN_SET_CURRENT_EDIT_PROFIL_SCI]: (state, { uiid, prf }: { uiid: string; prf: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].currentEditProfilSci = prf
  },
  [types.UI_MISSIONPLAN_SET_CURRENT_EDIT_STEP]: (state, { uiid, step }: { uiid: string; step: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].currentEditStep = step
  },
  [types.UI_MISSIONPLAN_SET_CURRENT_EDIT_WPT]: (state, { uiid, wpt }: { uiid: string; wpt: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].currentEditWpt = wpt
  },
  [types.UI_MISSIONPLAN_SET_CURRENT_EDIT_SENSOR]: (state, { uiid, sensor }: { uiid: string; sensor: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].currentEditSensor = sensor
  },
  [types.UI_MISSIONPLAN_SET_DRAWMOD]: (state, { uiid, drawmode }: { uiid: string; drawmode: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].drawMode = drawmode
  },
  [types.UI_MISSIONPLAN_SET_SHOW_GLOBALWPT]: (state, { uiid, show }: { uiid: string; show: boolean }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showGlobalWpts = show
  },
  [types.UI_MISSIONPLAN_SET_LASTACTION_ID]: (state, { uiid, actId }: { uiid: string; actId: number }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].lastActionId = actId
  },
  [types.UI_MISSIONPLAN_SET_MISSIONPLAN]: (state, { uiid, plan }: { uiid: string; plan: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].missionPlan = plan
      if (!state.uistates[uiid].payloadAutoSet) {
        state.uistates[uiid].enablePayload = (plan?.payload?.length ?? 0) > 0
        state.uistates[uiid].payloadAutoSet = true
      }
    }
  },
  [types.UI_SET_ITEM_MAP_STYLE]: (
    state,
    { style, uiid, colorType, map_uiids }: { style: any; uiid: string; colorType: string; map_uiids: Array<string> },
  ) => {
    if (colorType == 'mission') state.uistates[uiid].mapStyles = style
    map_uiids.forEach(map_uiid => (state.uistates[map_uiid].refreshMap = !state.uistates[map_uiid].refreshMap))
  },
  [types.UI_SET_DEFAULT_MAP_STYLE]: (state, { style, uiid }: { style: string; uiid: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].defaultMapStyles = style
      state.uistates[uiid].refreshMap = !state.uistates[uiid].refreshMap
    }
  },
  [types.UI_SET_GRA_ABSCISSE]: (state, { uiid, abscisse }: { uiid: string; abscisse: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].variableX = abscisse
  },
  [types.UI_SET_GRA_ORDONNEE]: (state, { uiid, ordonnee }: { uiid: string; ordonnee: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].variableY = ordonnee
  },
  [types.UI_SET_GRA_COLOR]: (state, { uiid, color }: { uiid: string; color: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].variableColor = color
  },
  [types.UI_SET_GRA_TRANSFORM]: (state, { uiid, transform }: { uiid: string; transform: ZoomTransform }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].transform = transform
  },
  [types.UI_SET_GRA_ZOOM]: (state, { uiid, zoom }: { uiid: string; zoom: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].zoom = zoom
  },
  [types.UI_SET_GRA_DATA]: (state, { uiid, name, value }: { uiid: string; name: string; value: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid][name] = value
  },
  [types.UI_SET_SCI_HOVERDATA]: (state, { uiid, hoverData }: { uiid: string; hoverData: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].sciHoverData = hoverData
  },
  [types.UI_SET_NAV_HOVERDATA]: (state, { uiid, hoverData }: { uiid: string; hoverData: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].navHoverData = hoverData
  },
  [types.UI_SET_SIMU_HOVERDATA]: (state, { uiid, hoverData }: { uiid: string; hoverData: any }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].simuHoverData = hoverData
  },
  [types.UI_SET_GRA_POINT_SIZE]: (state, { uiid, size }: { uiid: string; size: number }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].pointSize = size
  },
  [types.UI_TOGGLE_GRA_VORONOI]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].useVoronoi = !state.uistates[uiid].useVoronoi
  },
  [types.UI_SET_GRA_STATE]: (state, { uiid, config }: { uiid: string; config: GraphUiState }) => {
    if (config && state.uistates[uiid]) {
      state.uistates[uiid] = config
    }
  },
  [types.UI_SET_GRA_CONFIG]: (state, { uiid, config }: { uiid: string; config: any }) => {
    if (config && state.uistates[uiid]) {
      if (config.abscisse) state.uistates[uiid].variableX = config.abscisse
      if (config.ordonnee) state.uistates[uiid].variableY = config.ordonnee
      if (config.color) state.uistates[uiid].variableColor = config.color
      if (config.transform) state.uistates[uiid].transform = config.transform
      if (config.zoom) state.uistates[uiid].zoom = config.zoom
      if (config.size) state.uistates[uiid].pointSize = config.size
      if (config.dataLimit) state.uistates[uiid].dataLimit = config.dataLimit
      if (config.autoScaleX) state.uistates[uiid].autoScaleX = config.autoScaleX
      if (config.autoScaleY) state.uistates[uiid].autoScaleY = config.autoScaleY

      /* public typeUiid:string
    public refresh:boolean
    public variableX:string
    public variableY:string
    public variableColor:string
    public transform:ZoomTransform
    public zoom:string
    public pointSize:number
    public useVoronoi:boolean
    public colorSingle:string
    public colorMin:string
    public colorMid:string
    public colorMax:string
    public custDomainX:Array<number>|null
    public custDomainY:Array<number>|null
    public custDomainColor:Array<number>|null
    public dataLimit:string*/
    }
  },
  [types.UI_SET_NMEA_GRA_CONFIG]: (state, { uiid, config }: { uiid: string; config: any }) => {
    if (config && state.uistates[uiid]) {
      if (config.field && config.nmea) {
        const index = state.uistates[uiid].fields.findIndex(
          //@ts-ignore
          f => f.nmea == config.nmea && f.field.name == config.field.name,
        )
        if (index >= 0) state.uistates[uiid].fields.splice(index, 1)
        else state.uistates[uiid].fields.push({ nmea: config.nmea, field: config.field })
      }
    }
  },
  [types.UI_SET_SIMULATION_GRA_TYPE]: (state, { uiid, type }: { uiid: string; type: string }) => {
    if (type && state.uistates[uiid]) {
      const index = state.uistates[uiid].fields.findIndex((f: string) => f == type)
      if (index >= 0) state.uistates[uiid].fields.splice(index, 1)
      else state.uistates[uiid].fields.push(type)
    }
  },
  [types.UI_SET_COORDFORMATER]: (state, formater: string) => {
    state.coordFormatFilter = formater
    localStorage.setItem('coordFormatFilter', formater)
  },
  [types.UI_SET_MEASURETOOL]: (state, tool: string) => {
    state.refreshMesureTool = !state.refreshMesureTool
    state.measureTool = 'NONE'
    Vue.nextTick(() => {
      state.measureTool = tool
    })
  },
  [types.UI_TOGGLE_ONGOING_MISSION_VIEW]: state => {
    state.ongoingMissionActive = !state.ongoingMissionActive
  },
  [types.UI_ACTIVE_ONGOING_MISSION_VIEW]: state => {
    state.ongoingMissionActive = true
  },
  [types.UI_ACTIVE_SIMULATED_MISSION_VIEW]: state => {
    state.simulatedMissionActive = true
  },
  [types.UI_UNACTIVE_ONGOING_MISSION_VIEW]: state => {
    state.ongoingMissionActive = false
  },
  [types.UI_UNACTIVE_SIMULATED_MISSION_VIEW]: state => {
    state.simulatedMissionActive = false
  },
  [types.UI_TOGGLE_GRATICULE_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showGraticule = !state.uistates[uiid].showGraticule
  },
  [types.UI_SHOW_GRATICULE]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showGraticule = true
  },
  [types.UI_HIDE_GRATICULE]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showGraticule = false
  },
  [types.UI_ENSURE_MISSION_STATE]: (state, { ids, typeUiid }: { ids: Array<number>; typeUiid: string }) => {
    ids.forEach(id => {
      const uiid = buildMissionUIStateId(id, typeUiid)
      if (!state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates, uiid, new MissionUiState({ typeUiid }))
    })
  },
  [types.UI_ENSURE_MISSIONGLOBAL_STATE]: (state, { ids, typeUiid }: { ids: Array<number>; typeUiid: string }) => {
    ids.forEach(id => {
      const uiid = buildSimulationUIStateId(id, typeUiid)
      if (!state.uistates.hasOwnProperty(uiid)) {
        if (typeUiid == 'detail') {
          const leftCmp = SIMULATIONS_DETAIL_TABS.find(it => it.cmp == 'SimulationDetailMap')
          const newLeft = new UiTab(leftCmp)
          leftCmp!.id++

          Vue.set(
            state.uistates,
            uiid,
            new SimulationUiState({
              typeUiid,
              tabsleft: new UiTabs({
                tabs: [newLeft],
                selected: `${newLeft.cmp}-${newLeft.id}`,
              }),
            }),
          )
        } else {
          Vue.set(state.uistates, uiid, new SimulationUiState({ typeUiid }))
        }
      }
    })
  },
  [types.UI_ENSURE_FLEET_STATE]: (state, { ids, typeUiid }: { ids: Array<number>; typeUiid: string }) => {
    ids.forEach(id => {
      const uiid = buildFleetUIStateId(id, typeUiid)
      if (!state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates, uiid, new FleetUiState({ typeUiid }))
    })
  },
  [types.UI_ENSURE_MISSIONPLAN_STATE]: (state, { id, typeUiid }: { id: number; typeUiid: string }) => {
    const uiid = buildMissionPlanStateId(id, typeUiid)
    if (!state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates, uiid, new MissionPlanUiState({ typeUiid }))
  },
  [types.UI_ENSURE_GRA_STATE]: (state, { uiid, config }: { uiid: string; config: any }) => {
    if (!state.uistates.hasOwnProperty(uiid))
      Vue.set(
        state.uistates,
        uiid,
        new GraphUiState({ typeUiid: uiid.toLowerCase().startsWith('fleet') ? 'fleet' : 'detail', ...config }),
      )
  },
  [types.UI_ENSURE_MAP_STATE]: (state, uiid: string) => {
    if (!state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates, uiid, new MapUiState({ typeUiid: 'detail' }))
  },
  [types.UI_ENSURE_FLEETMAP_STATE]: (state, uiid: string) => {
    if (!state.uistates.hasOwnProperty(uiid)) Vue.set(state.uistates, uiid, new MapUiState({ typeUiid: 'fleet' }))
  },
  [types.UI_ENSURE_ALL_GRA_STATE]: (state, uiid: string) => {
    if (!state.uistates.hasOwnProperty(uiid))
      Vue.set(
        state.uistates,
        uiid,
        new AllGraphUiState({ typeUiid: uiid.toLowerCase().startsWith('fleet') ? 'fleet' : 'detail' }),
      )
  },
  [types.UI_ENABLE_CONSOLE_MSG]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].canLogConsoleMsg = true
  },
  [types.UI_LOAD_CONSOLE_MSG_REQUEST]: (state, id: number) => {
    const uiid = buildMissionUIStateId(id, 'detail')
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].consoleMsgsLoading = true
  },
  [types.UI_LOAD_CONSOLE_MSG_SUCCESS]: (state, { id, rows }: { id: number; rows: Array<ConsoleRow> }) => {
    const uiid = buildMissionUIStateId(id, 'detail')
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].consoleMsgs.splice(0, 0, ...rows)
      state.uistates[uiid].consoleMsgsLoading = false
    }
  },
  [types.UI_LOAD_CONSOLE_MSG_ERROR]: (state, id: number) => {
    const uiid = buildMissionUIStateId(id, 'detail')
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].consoleMsgsLoading = false
  },
  [types.UI_SET_MOVABLE_DIALOG_VISIBILITY]: (state, isVisible: boolean) => {
    state.currentMovableDialog.isVisible = isVisible
  },
  [types.UI_SHOW_DEPTH_PROFILE_DIALOG]: state => {
    state.currentMovableDialog = {
      titleKey: 'Pilot.DepthProfile.DialogResultTitle',
      cmp: 'DepthProfilDialog',
      uiid: null,
      isVisible: true,
    }
  },
  [types.UI_SHOW_CHART_CONFIG_DIALOG]: (state, uiid: string) => {
    state.currentMovableDialog = {
      titleKey: 'Pilot.ChartConfig.Title',
      cmp: 'ChartConfig',
      uiid,
      isVisible: true,
    }
  },
  [types.UI_SET_INFOS_DIALOG_VISIBILITY]: (state, isVisible: boolean) => {
    state.currentInfosDialog.isVisible = isVisible
  },
  [types.UI_SHOW_INFOS_DIALOG]: (
    state,
    { title, infos, urlinfos }: { title: string; infos: string; urlinfos: string },
  ) => {
    state.currentInfosDialog = {
      title: title,
      infos: infos,
      urlinfos: urlinfos,
      isVisible: true,
    }
  },
  [types.UI_REFRESH_COMPONENT]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      state.uistates[uiid].refresh = !state.uistates[uiid].refresh
    }
  },
  [types.UI_RESET_UISTATE]: (state, uiid: string) => {
    Vue.delete(state.uistates, uiid)
  },
  [types.UI_SET_KEYPRESS]: (state, { code, pressed }: { code: string; pressed: boolean }) => {
    Vue.set(state.keys, code, pressed)
  },
  [types.UI_TOGGLE_MISSION_INFO_PANEL_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].infosPanelVisible = !state.uistates[uiid].infosPanelVisible
  },
  [types.UI_SET_PLANIFVIEW_MODE]: (state, { uiid, mode }: { uiid: string; mode: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].planifViewMode = mode
  },
  [types.UI_TOGGLE_PATH_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showPath = !state.uistates[uiid].showPath
  },
  [types.UI_TOGGLE_MORE_NMEA_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showMoreNmea = !state.uistates[uiid].showMoreNmea
  },
  [types.UI_TOGGLE_REMAINING_NAV_VISIBILITY]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid))
      state.uistates[uiid].showRemainingNavigation = !state.uistates[uiid].showRemainingNavigation
  },
  [types.UI_SHOW_PATH]: (state, uiid: string) => {
    state.uistates[uiid].showPath = true
  },
  [types.UI_HIDE_PATH]: (state, uiid: string) => {
    if (state.uistates.hasOwnProperty(uiid)) state.uistates[uiid].showPath = false
  },
  [types.UI_SET_GRAPH_HOVER_DATA]: (state, graphHover: HoverData) => {
    state.graphHover = { ...graphHover }
  },
  [types.UI_SET_GRAPH_HOVER_DATA_VISIBILITY]: (state, visible: boolean) => {
    state.graphHover.hover = visible
  },
  [types.UI_SET_LEFTPANEL_SIZE]: (state, { uiid, size }: { uiid: string; size: string }) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      const detailPanelState = state.uistates[uiid] as SimulationUiState
      detailPanelState.leftPanelWidth = size
    }
  },
  [types.UI_NAVTO_DETAIL]: (
    state,
    { uiid, cmp, isleft, id, label }: { uiid: string; cmp: string; isleft: boolean; id: number; label: string },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      const detailPanelState = state.uistates[uiid] as SimulationUiState
      const tabsUI = isleft ? detailPanelState.tabsleft : detailPanelState.tabsright
      const newIdx = tabsUI.tabs.findIndex(it => it.cmp == cmp && it.id == id)
      //console.log("newIdx",newIdx)
      if (newIdx == -1) {
        const cmpfound = SIMULATIONS_DETAIL_TABS.find(it => it.cmp == cmp)
        //console.log("cmpfound",cmpfound,cmp)
        if (cmpfound) {
          const newComp = new UiTab({ ...cmpfound, label: label || cmpfound.label })
          cmpfound.id++ //augmente l'id pour la prochaine creation
          tabsUI.tabs.push(newComp)
          tabsUI.selected = `${cmp}-${newComp.id}`
          return
        }
      }
      tabsUI.selected = `${cmp}-${id}`
    }
  },
  [types.UI_UNSELECT_TAB]: (state, tab: UiTab) => {
    tab.tryClose = !tab.tryClose
  },
  [types.UI_CLOSE_TAB]: (
    state,
    { uiid, cmp, isleft, id }: { uiid: string; cmp: string; isleft: boolean; id: number },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      const detailPanelState = state.uistates[uiid] as SimulationUiState
      const tabsUI = isleft ? detailPanelState.tabsleft : detailPanelState.tabsright
      const newIdx = tabsUI.tabs.findIndex(it => it.cmp == cmp && it.id == id)
      if (newIdx > -1) {
        tabsUI.tabs.splice(newIdx, 1)
      }
      if (tabsUI.selected == `${cmp}-${id}`)
        tabsUI.selected = tabsUI.tabs.length > 0 ? `${tabsUI.tabs[0].cmp}-${tabsUI.tabs[0].id}` : 'SimulationMenu-0'
    }
  },
  [types.UI_MOVE_TAB]: (
    state,
    { uiid, cmp, isleft, id }: { uiid: string; cmp: string; isleft: boolean; id: number },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      const detailPanelState = state.uistates[uiid] as SimulationUiState
      const tabsUI = isleft ? detailPanelState.tabsleft : detailPanelState.tabsright
      const newIdx = tabsUI.tabs.findIndex(it => it.cmp == cmp && it.id == id)
      if (newIdx > -1) {
        const tagetTabsUI = isleft ? detailPanelState.tabsright : detailPanelState.tabsleft
        const selectedTab = tabsUI.tabs.splice(newIdx, 1)[0]
        if (tabsUI.selected == `${cmp}-${id}`)
          tabsUI.selected = tabsUI.tabs.length > 0 ? `${tabsUI.tabs[0].cmp}-${tabsUI.tabs[0].id}` : 'SimulationMenu-0'
        //on attend le next tick pour reconstruir le composant pour etre certain que les listener on bien été clear avant la reconstruction
        Vue.nextTick(() => {
          tagetTabsUI.tabs.push(selectedTab)
          tagetTabsUI.selected = `${cmp}-${id}`
        })
      }
    }
  },
  [types.UI_RENAME_DETAIL]: (
    state,
    { uiid, cmp, isleft, id, label }: { uiid: string; cmp: string; isleft: boolean; id: number; label: string },
  ) => {
    if (state.uistates.hasOwnProperty(uiid)) {
      const detailPanelState = state.uistates[uiid] as SimulationUiState
      const tabsUI = isleft ? detailPanelState.tabsleft : detailPanelState.tabsright
      const newIdx = tabsUI.tabs.findIndex(it => it.cmp == cmp && it.id == id)
      if (newIdx > -1) {
        tabsUI.tabs[newIdx].label = label
      }
    }
  },
  [types.UI_FLEET_LOGBOOK_REQUEST]: (state, id) => {
    //state.status = `loading logbook from fleet ${id}`
  },
  [types.UI_FLEET_LOGBOOK_SUCCESS]: (state, { id, datas }: { id: number; datas: AloLogbookItem[] }) => {
    //state.status = `success loading logbook from fleet  ${id}`
    const uiid = buildFleetUIStateId(id, 'detail')
    if (state.uistates.hasOwnProperty(uiid)) {
      const fleetUiState = state.uistates[uiid] as FleetUiState
      //Mise à jour des données file status
      fleetUiState.logBookItems = datas
      fleetUiState._logBookLoaded = true
    }
  },
  [types.UI_FLEET_LOGBOOK_ERROR]: (state, id: number) => {
    //state.status = `error loading logbook from fleet ${id}`
  },
  [types.UI_FLEET_LOGBOOK_ITEM_SUCCESS]: (state, { fleId, data }: { fleId: number; data: AloLogbookItem }) => {
    //state.status = `success loading logbookitem ${data.lbkId} from fleet  ${fleId}`
    const uiid = buildFleetUIStateId(fleId, 'detail')
    if (state.uistates.hasOwnProperty(uiid)) {
      const fleetUiState = state.uistates[uiid] as FleetUiState
      const indexLog = fleetUiState.logBookItems.findIndex(m => m.lbkId == data.lbkId)
      fleetUiState.logBookItems.splice(
        indexLog == -1 ? fleetUiState.logBookItems.length : indexLog,
        indexLog == -1 ? 0 : 1,
        data,
      )
    }
  },
  [types.UI_FLEET_LOGBOOK_SEND_MESSAGE_REQUEST]: (state, fleId: number) => {
    //state.status = `send message to logbook from fleet ${fleId}`
  },
  [types.UI_FLEET_LOGBOOK_SEND_MESSAGE_ERROR]: (state, fleId: number) => {
    //state.status = `error send message to logbook from fleet ${fleId}`
  },
  [types.UI_FLEET_LOGBOOK_UPDATE_MESSAGE_REQUEST]: (state, { fleId, lbkId }: { fleId: number; lbkId: number }) => {
    //state.status = `update message ${lbkId} to logbook from fleet ${fleId}`
  },
  [types.UI_FLEET_LOGBOOK_UPDATE_MESSAGE_ERROR]: (state, { fleId, lbkId }: { fleId: number; lbkId: number }) => {
    //state.status = `update message ${lbkId} to logbook from fleet ${fleId}`
  },
  [types.UI_FLEET_LOGBOOK_ITEM_ERROR]: (state, { fleId, logBookId }: { fleId: number; logBookId: number }) => {
    //state.status = `error loading logbookitem ${logBookId} from fleet ${fleId}`
  },
  [types.UI_FLEET_LOGBOOK_ITEM_REQUEST]: (state, { fleId, logBookId }: { fleId: number; logBookId: number }) => {
    //state.status = `loading logbookitem ${logBookId} from fleet ${fleId}`
  },
}

export default mutations
